import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import GlobeIcon from '@atlaskit/icon/core/globe';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, useProductConfig } from '../../../../../common/constants/products';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { serveConfluenceSpaceIconUpdate } from '../../../../../common/utils/experiments';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store';
import { filter as spaceFilter } from '../../../../../controllers/store/search/filters/space';

import { i18n } from './messages';

export const SpaceFilter = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const [
		filterData,
		{
			loadInitialOptions,
			setLookupInput,
			setOption: setOptionForSelectFilter,
			clearLookupInput,
			clearOptions,
		},
	] = useSelectFilter({
		filterId: spaceFilter.id,
	});

	const confluenceProductConfig = useProductConfig(ProductKeys.Confluence);
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		badgeLabel,
		firstSelectedOptionLabel,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.confluenceFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.confluenceFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	const ConfluenceIcon = confluenceProductConfig.Icon;

	const getIconBefore = () => {
		return (
			<FilterButtonIconContainer>
				<ConfluenceIcon size="xxsmall" />
				{serveConfluenceSpaceIconUpdate() ? (
					<GlobeIcon label={formatMessage(i18n.confluenceFilterSpaceIcon)} />
				) : (
					<FolderOpenIcon label={formatMessage(i18n.confluenceFilterSpaceIcon)} />
				)}
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			<ChevronDownIcon label="" size="small" />
		) : (
			badgeLabel && <Badge appearance="primary">{badgeLabel}</Badge>
		);
	};

	const setOption = (option: SelectFilterOption | SelectFilterOption[]) => {
		setOptionForSelectFilter(option, undefined, ProductKeys.Confluence);
	};

	return (
		<FilterPopupSelect
			filterId={spaceFilter.id}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={formatMessage(i18n.space)}
			placeholder={formatMessage(i18n.confluenceFilterSpacePlaceholder)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={clearOptions}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					testId="search-dialog-space-filter-button"
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
					minWidthZero={true}
				>
					{firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.space)}
				</FilterButton>
			)}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
		/>
	);
};
